<template>
    <div class="data-evaluation-page">
        <!-- 数据表字段详情 -->
        <el-card class="box-card" shadow="never">
            <div slot="header" class="clearfix">
                <span>数据表字段详情</span>
            </div>
            <el-row :gutter="20">
                <el-col :span="3">
                    <div style="margin-bottom: 10px;">
                        <span style="color: red;">*</span> 字段详情表上传：
                    </div>
                </el-col>
                <el-col :span="9">
                    <!-- 上传区域 -->
                    <el-upload ref="upload" drag action='#' :auto-upload="false" :on-change="handleAnalysisFileChange"
                        :on-remove="handleRemove" :file-list="analysisFileList" :limit="10" :multiple="true"
                        :show-file-list="false" accept=".xlsx,.xls">
                        <i class="el-icon-upload"></i>
                        <div class="el-upload__text">点击或拖拽文件到此处<br>
                            <span style="color: gray; font-size: 10px;">
                                支持 Excel(.xlsx, .xls) 文件格式 文件大小不得超过{{ fileSizeLimit }}M
                            </span>
                        </div>
                    </el-upload>
                    <!-- 底部按钮 -->
                    <el-link type="primary" :underline="true" @click="downloadTemplate('FieldDetailTemplate.xlsx')">
                        《数据字段详情模板》下载...
                    </el-link>
                </el-col>
                <el-col :span="12">
                    <el-row :gutter="20" style="min-height: 170px">
                        <p>已选择文件:</p>
                        <div v-if="analysisFileList.length > 0" style="max-height: 130px; overflow-y: auto;">
                            <el-table :data="analysisFileList" style="width: 100%" border>
                                <el-table-column prop="name" label="文件名" width="300">
                                    <template slot-scope="scope">
                                        <span style="display: flex; align-items: center;">
                                            <i :class="getFileTypeIcon(scope.row.name)" style="margin-right: 8px;"></i>
                                            {{ scope.row.name }}
                                            <el-tag v-if="scope.row.parsed" type="success" size="mini"
                                                style="margin-left: 5px;">
                                                已解析
                                            </el-tag>
                                        </span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="size" label="大小" width="119">
                                    <template slot-scope="scope">
                                        {{ formatFileSize(scope.row.size) }}
                                    </template>
                                </el-table-column>
                                <el-table-column label="状态" width="100">
                                    <template slot-scope="scope">
                                        <el-tag v-if="scope.row.status === 'ready'" type="info"
                                            size="small">待解析</el-tag>
                                        <el-tag v-if="scope.row.status === 'parsing'" type="warning"
                                            size="small">解析中</el-tag>
                                        <el-tag v-if="scope.row.status === 'parsed'" type="success"
                                            size="small">解析完成</el-tag>
                                        <el-tag v-if="scope.row.status === 'error'" type="danger"
                                            size="small">解析失败</el-tag>
                                        <el-progress
                                            v-if="scope.row.status === 'uploading' && scope.row.percentage < 100"
                                            :percentage="scope.row.percentage" :show-text="false" :stroke-width="6">
                                        </el-progress>
                                    </template>
                                </el-table-column>
                                <el-table-column label="操作" width="120">
                                    <template slot-scope="scope">
                                        <el-button type="text" @click="parseSingleFile(scope.$index)"
                                            :disabled="scope.row.status === 'parsing' || scope.row.status === 'uploading'">
                                            {{ scope.row.parsed ? '重新解析' : '解析' }}
                                        </el-button>
                                        <el-button type="text" @click="removeAnalysisFile(scope.$index)"
                                            :disabled="scope.row.status === 'parsing' || scope.row.status === 'uploading'">
                                            删除
                                        </el-button>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                        <div v-else
                            style="display: flex; align-items: center; justify-content: center; color: #909399; height: 100px;">
                            暂无文件
                        </div>
                    </el-row>
                    <el-row :gutter="20" type="flex" justify="center" style="margin-top: 10px;">
                        <el-button @click="clearAnalysisFiles"
                            :disabled="analysisFileList.length === 0">清空列表</el-button>
                        <el-button type="primary" @click="handleAnalysis"
                            :disabled="analysisFileList.length === 0 || analysing" :loading="analysing">
                            {{ analysing ? '解析中...' : '批量解析' }}
                        </el-button>
                    </el-row>
                </el-col>
            </el-row>

            <!-- 解析结果表格 -->
            <div v-if="tableData.length > 0" style="margin-top: 20px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                    <span style="font-weight: bold; color: #333;">
                        解析结果 (共 {{ tableData.length }} 条记录，来自 {{new Set(tableData.map(item => item.文件名 ||
                        item.文件)).size }} 个文件)
                    </span>
                    <el-button type="text" @click="clearParsedData" :disabled="tableData.length === 0">
                        清空解析结果
                    </el-button>
                </div>
                <el-table :data="tableData" border style="width: 100%" height="600">
                    <el-table-column prop="文件名" label="来源文件" width="150" fixed>
                        <template slot-scope="scope">
                            <span>{{ scope.row.文件名 }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="表名" label="表名" width="180" />
                    <el-table-column prop="字段名" label="字段名" width="130" />
                    <el-table-column prop="字段格式" label="字段格式" width="120" />
                    <el-table-column prop="字段说明" label="字段说明" min-width="200" />
                    <el-table-column prop="取值范围" label="取值范围" width="150" />
                    <el-table-column prop="字段关系（表内）" label="字段关系（表内）" width="160" />
                    <el-table-column prop="字段关系（关联表）" label="字段关系（关联表）" width="160" />
                    <el-table-column prop="是否为核心字段" label="是否为核心字段" width="120" />
                    <el-table-column prop="是否为周期字段" label="是否为周期字段" width="120" />
                </el-table>
            </div>
        </el-card>

        <!-- 抽样数据 -->
        <el-card class="box-card" shadow="never" style="margin-top: 20px;">
            <div slot="header" class="clearfix">
                <span>抽样数据</span>
            </div>
            <el-row :gutter="10">
                <el-col :span="3">
                    <div style="margin-bottom: 10px;">
                        <span style="color: red;">*</span> 抽样数据文件上传：
                    </div>
                </el-col>
                <el-col :span="21">
                    <!-- 上传区域 -->
                    <el-upload ref="upload" drag action='#' :auto-upload="false" :on-change="handleFileChange"
                        :on-remove="handleRemove" :file-list="fileList" :limit="10" :multiple="true" accept=".xlsx,.xls"
                        :show-file-list="false" :disabled="uploading"
                        style="border: 2px dashed #d9d9d9; padding: 40px 0; text-align: center;">
                        <i class="el-icon-upload"></i>
                        <div class="el-upload__text">点击或拖拽文件到此处</div>
                    </el-upload>
                </el-col>
            </el-row>

            <!-- 待上传文件列表 -->
            <div v-if="fileList.length > 0" style="margin-top: 20px;">
                <p>待上传文件 ({{ fileList.length }})</p>
                <el-table :data="fileList" style="width: 100%" border>
                    <el-table-column prop="name" label="文件名" width="300">
                        <template slot-scope="scope">
                            <span style="display: flex; align-items: center;">
                                <i :class="getFileTypeIcon(scope.row.name)" style="margin-right: 8px;"></i>
                                {{ scope.row.name }}
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="size" label="大小" width="120">
                        <template slot-scope="scope">
                            {{ formatFileSize(scope.row.size) }}
                        </template>
                    </el-table-column>
                    <el-table-column label="状态" width="150">
                        <template slot-scope="scope">
                            <el-tag v-if="scope.row.status === 'ready'" type="info" size="small">待上传</el-tag>
                            <el-progress v-if="scope.row.status === 'uploading' && scope.row.percentage < 100"
                                :percentage="scope.row.percentage" :show-text="false" :stroke-width="6"></el-progress>
                            <el-tag v-else-if="scope.row.status === 'success'" type="success" size="small">上传完成</el-tag>
                            <el-tag v-else-if="scope.row.status === 'error'" type="danger" size="small">上传失败</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="操作" width="80">
                        <template slot-scope="scope">
                            <el-button type="text" @click="removeFile(scope.$index)"
                                :disabled="scope.row.status === 'uploading'">
                                删除
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>
            </div>


            <div style="margin-top: 20px; text-align: right; ">
                <el-button @click="clearFiles">清空列表</el-button>
            </div>
        </el-card>

        <!-- 冷数据处理 -->
        <el-card class="box-card" shadow="never" style="margin-top: 20px;">
            <div slot="header" class="clearfix">
                <span>数据处理</span>
            </div>
            <el-row :gutter="0">
                <el-col :span="2">
                    <div style="margin-top: 5px; font-size: 15px;">
                        <span style="color: red;">*</span> 冷数据处理方式：
                    </div>
                </el-col>
                <el-col :span="22">
                    <el-input type="textarea" :rows="3" v-model="coldDataMethod"
                        placeholder="请说明冷数据的存储、处理和安全措施... 例如：存储位置--腾讯云归档存储/本地机房数据库存储；保留期限--10年；运维保障--每周日02:00全量备份至阿里云OSS归档存储"></el-input>
                </el-col>
            </el-row>
        </el-card>

        <!-- 新增：提交质量评估按钮 -->
        <div style="margin-top: 30px; text-align: center;">
            <el-button type="danger" size="medium" @click="close('/DataQuality')" :disabled="isSubmitDisabled" style="width: 80px;">
                关闭
            </el-button>
            <el-button type="primary" size="medium" @click="submitEvaluation" :loading="submitting"
                :disabled="isSubmitDisabled" style="width: 150px;">
                {{ submitting ? '提交中...' : '提交质量评估' }}
            </el-button>
        </div>
    </div>
</template>

<script>
import * as XLSX from 'xlsx';
export default {
    name: 'DataQualityApply',
    data() {
        return {
            fileSizeLimit: 100, // 文件大小限制，单位MB
            analysisFileList: [], // 存储需要解析的文件列表
            analysing: false, // 是否正在解析文件
            tableData: [], // 解析文件后表格数据
            error: '',  // 解析错误信息

            coldDataMethod: '', // 冷数据处理方式

            registrationId: '',
            userId: '',
            companyAndAssetInfo: {},

            fileList: [],
            uploading: false,
            submitting: false, // 提交中状态
            uploadedFileUrl: {
                dataFieldFile: [],  // 数据字段文件URL
                dataDetailedFile: [] // 抽样数据文件URL
            },
             submitConfirmInstance: null, // 保存提交确认弹窗的引用

        }
    },
    created() {
        // 从路由中获取传递的参数
        this.registrationId = this.$route.query.registrationId || '';
        this.userId = this.$route.query.userId || '';

        if (this.registrationId && this.userId) {
            this.initializeApplication();
        }
    },
    methods: {
        initializeApplication() {
            this.getRequest('/web/dataQualityDetail/query?registrationId=' + this.registrationId).then(resp => {
                if (resp.data) {
                    console.log(resp.data);
                }
            }).catch(error => {
                console.error('获取数据失败:', error);
                this.companyAndAssetInfo = {};
            });
        },
        // 处理分析文件选择的方法
        handleAnalysisFileChange(file, fileList) {
            // 校验文件类型
            const isExcel = /\.(xlsx|xls)$/i.test(file.name);
            if (!isExcel) {
                this.$message.error('仅支持 .xlsx 或 .xls 文件格式！');
                // 从fileList中移除不符合条件的文件
                const index = fileList.indexOf(file);
                if (index > -1) {
                    fileList.splice(index, 1);
                }
                return;
            }

            // 校验文件大小
            const isLtLimit = file.size / 1024 / 1024 < this.fileSizeLimit;
            if (!isLtLimit) {
                this.$message.error(`上传文件大小不能超过 ${this.fileSizeLimit}MB！`);
                // 从fileList中移除不符合条件的文件
                const index = fileList.indexOf(file);
                if (index > -1) {
                    fileList.splice(index, 1);
                }
                return;
            }

            // 检查是否已存在同名文件
            const duplicate = this.analysisFileList.some(f => f.name === file.name);
            if (duplicate) {
                this.$message.warning(`文件 "${file.name}" 已存在，请勿重复上传`);
                return;
            }

            // 设置文件初始状态
            file.status = 'ready';
            file.percentage = 0;
            file.parsed = false; // 新增：标记是否已解析
            file.parsedData = []; // 新增：存储该文件解析的数据

            this.analysisFileList = [...fileList];
        },
        // 处理删除分析文件的方法
        removeAnalysisFile(index) {
            const file = this.analysisFileList[index];

            // 如果文件已解析，则从tableData中删除对应的数据
            if (file.parsed && file.parsedData && file.parsedData.length > 0) {
                // 从表格数据中删除该文件的数据
                this.tableData = this.tableData.filter(item => item.文件名 !== file.name);
            }

            // 从文件列表中删除
            this.analysisFileList.splice(index, 1);

            this.$message.success('文件已删除');
        },
        // 清空分析文件列表的方法
        clearAnalysisFiles() {
            this.analysisFileList = [];
            this.tableData = [];
            this.$message.success('文件列表已清空');
        },
        // 清空解析数据
        clearParsedData() {
            this.tableData = [];
            // 重置所有文件的解析状态
            this.analysisFileList.forEach(file => {
                file.parsed = false;
                file.parsedData = [];
                file.status = 'ready';
            });
            this.$message.success('解析结果已清空');
        },
        // 解析单个文件
        parseSingleFile(index) {
            const file = this.analysisFileList[index];
            this.parseExcelFile(file, index);
        },
        // 解析文件的方法
        parseExcelFile(file, fileIndex) {
            return new Promise((resolve, reject) => {
                const rawFile = file.raw || file;

                // 设置文件状态为解析中
                this.$set(this.analysisFileList[fileIndex], 'status', 'parsing');

                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const data = new Uint8Array(e.target.result);
                        const workbook = XLSX.read(data, { type: 'array' });

                        // 读取第一个 Sheet
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];

                        // 转为二维数组（header: 1 表示第一行为表头）
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                        if (!jsonData || jsonData.length < 2) {
                            this.$set(this.analysisFileList[fileIndex], 'status', 'error');
                            this.$message.error(`文件 ${file.name} 内容为空或格式不正确（需至少两行：表头+数据）`);
                            reject(new Error('文件内容为空或格式不正确'));
                            return;
                        }

                        // 提取表头（第一行）
                        const headers = jsonData[0].map(h => (h || '').toString().trim());
                        const rows = jsonData.slice(1);

                        // 转换为对象数组，并添加文件名信息
                        const parsedData = rows
                            .filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ''))
                            .map(row => {
                                const obj = {};
                                headers.forEach((header, index) => {
                                    obj[header] = (row[index] !== undefined && row[index] !== null)
                                        ? row[index].toString().trim()
                                        : '';
                                });
                                // 添加文件名信息
                                obj['文件名'] = file.name;
                                return obj;
                            });

                        // 保存解析数据到文件对象
                        this.$set(this.analysisFileList[fileIndex], 'parsedData', parsedData);
                        this.$set(this.analysisFileList[fileIndex], 'parsed', true);
                        this.$set(this.analysisFileList[fileIndex], 'status', 'parsed');

                        // 合并到表格数据
                        this.mergeParsedData();

                        this.$message.success(`文件 ${file.name} 解析成功，共 ${parsedData.length} 条记录`);
                        resolve(parsedData);
                    } catch (err) {
                        console.error(`解析文件 ${file.name} 失败:`, err);
                        this.$set(this.analysisFileList[fileIndex], 'status', 'error');
                        this.$message.error(`文件 ${file.name} 解析失败：请检查文件结构`);
                        reject(err);
                    }
                };

                reader.onerror = (err) => {
                    console.error(`读取文件 ${file.name} 失败:`, err);
                    this.$set(this.analysisFileList[fileIndex], 'status', 'error');
                    this.$message.error(`文件 ${file.name} 读取出错`);
                    reject(err);
                };

                reader.readAsArrayBuffer(rawFile);
            });
        },
        // 合并所有已解析文件的数据
        mergeParsedData() {
            // 清空现有表格数据
            this.tableData = [];

            // 合并所有已解析文件的数据
            this.analysisFileList.forEach(file => {
                if (file.parsed && file.parsedData && file.parsedData.length > 0) {
                    this.tableData = this.tableData.concat(file.parsedData);
                }
            });
        },
        // 批量解析所有文件
        async handleAnalysis() {
            if (this.analysisFileList.length === 0) {
                this.$message.warning('请先选择文件');
                return;
            }

            this.analysing = true;
            this.error = '';

            try {
                const unparsedFiles = this.analysisFileList.filter(file => !file.parsed);

                if (unparsedFiles.length === 0) {
                    this.$message.info('所有文件均已解析完成');
                    this.analysing = false;
                    return;
                }

                // 批量解析未解析的文件
                for (let i = 0; i < this.analysisFileList.length; i++) {
                    const file = this.analysisFileList[i];
                    if (!file.parsed) {
                        try {
                            await this.parseExcelFile(file, i);
                        } catch (error) {
                            console.error(`解析文件 ${file.name} 失败:`, error);
                            continue; // 继续解析其他文件
                        }
                    }
                }

                this.$message.success('批量解析完成');
            } catch (error) {
                console.error('批量解析出错:', error);
                this.$message.error('批量解析过程中发生错误');
            } finally {
                this.analysing = false;
            }
        },
        formatDateTime(dateTime) {
            if (!dateTime) return '-'

            const date = new Date(dateTime)
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')

            return `${year}-${month}-${day}`
        },

        // 模板下载
        async downloadTemplate(customName) {
            try {
                const response = await this.$axios({
                    method: 'get',
                    url: '/web/template/download/' + customName,
                    responseType: 'blob'
                });

                // 优先使用传入的自定义文件名
                let fileName = customName;
                //这里把文件名替换成中文，上面用英文是避免网络传输乱码问题，这里换成中文文件名下载时就正常了
                if (customName.startsWith('FieldDetailTemplate.xlsx')) {
                    fileName = "数据字段详情模板.xlsx"
                }

                // 处理中文文件名编码问题
                const encodedFileName = encodeURIComponent(fileName);

                // 创建下载链接
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                link.setAttribute('download-filename', encodedFileName); // 兼容性处理
                document.body.appendChild(link);
                link.click();

                // 清理
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('下载失败:', error);
                this.$message.error('文件下载失败: ' + error.message);
            }
        },
        // 获取文件图标
        getFileTypeIcon(filename) {
            const ext = filename.split('.').pop().toLowerCase();
            if (ext === 'xlsx' || ext === 'xls') return 'el-icon-document';
            if (ext === 'docx') return 'el-icon-document';
            return 'el-icon-document';
        },
        // 格式化文件大小
        formatFileSize(size) {
            if (size < 1024) return size + ' B';
            if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
            return (size / 1024 / 1024).toFixed(1) + ' MB';
        },
        // 文件变更时
        handleFileChange(file, fileList) {
            const isExcel = /\.(xlsx|xls)$/i.test(file.name);
            const isLt10M = file.size / 1024 / 1024 < 100;

            if (!isExcel) {
                this.$message.error('仅支持 .xlsx、.xls！');
                return false;
            }

            if (!isLt10M) {
                this.$message.error('上传文件大小不能超过 100MB！');
                return false;
            }
            this.fileList = fileList.map(f => {
                f.percentage = 0;
                f.status = 'ready'; // ready / uploading / success / error
                return f;
            });
        },
        // 移除文件
        handleRemove(file, fileList) {
            this.fileList = fileList;
        },
        // 删除指定文件
        removeFile(index) {
            this.fileList.splice(index, 1);
        },
        // 清空文件列表
        clearFiles() {
            this.$refs.upload.clearFiles();
            this.fileList = [];
        },
        // 上传单个文件
        uploadSingleFile(file, fileType) {
            return new Promise((resolve, reject) => {
                // 创建表单数据
                const formData = new FormData();
                formData.append('registrationId', this.registrationId);
                formData.append('type', fileType);
                formData.append('file', file.raw || file);

                // 发送上传请求
                this.$axios.post('/web/dataQuality/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );

                        // 更新文件进度
                        const fileIndex = this[fileType === 'dataFieldFile' ? 'analysisFileList' : 'fileList']
                            .findIndex(f => f.uid === file.uid);
                        if (fileIndex !== -1) {
                            this.$set(this[fileType === 'dataFieldFile' ? 'analysisFileList' : 'fileList'][fileIndex], 'percentage', percentCompleted);
                            this.$set(this[fileType === 'dataFieldFile' ? 'analysisFileList' : 'fileList'][fileIndex], 'status', 'uploading');
                        }
                    }
                }).then(response => {
                    if (response.status === 200) {
                        // 更新文件状态为成功
                        const fileIndex = this[fileType === 'dataFieldFile' ? 'analysisFileList' : 'fileList']
                            .findIndex(f => f.uid === file.uid);
                        if (fileIndex !== -1) {
                            this.$set(this[fileType === 'dataFieldFile' ? 'analysisFileList' : 'fileList'][fileIndex], 'status', 'success');
                        }

                        // 保存文件URL
                        this.uploadedFileUrl[fileType].push(response.data.object);
                        resolve(response.data.object);
                    } else {
                        reject(new Error(response.msg || '文件上传失败'));
                    }
                }).catch(error => {
                    // 更新文件状态为失败
                    const fileIndex = this[fileType === 'dataFieldFile' ? 'analysisFileList' : 'fileList']
                        .findIndex(f => f.uid === file.uid);
                    if (fileIndex !== -1) {
                        this.$set(this[fileType === 'dataFieldFile' ? 'analysisFileList' : 'fileList'][fileIndex], 'status', 'error');
                    }
                    reject(error);
                });
            });
        },
        // 上传所有文件
        async uploadAllFiles() {
            this.uploading = true;
            this.uploadedFileUrl.dataFieldFile = [];
            this.uploadedFileUrl.dataDetailedFile = [];

            try {
                // 上传数据字段文件
                if (this.analysisFileList.length > 0) {
                    for (let file of this.analysisFileList) {
                        await this.uploadSingleFile(file, 'dataFieldFile');
                    }
                }

                // 上传抽样数据文件
                if (this.fileList.length > 0) {
                    for (let file of this.fileList) {
                        await this.uploadSingleFile(file, 'dataDetailedFile');
                    }
                }

                this.uploading = false;
                return true;
            } catch (error) {
                this.uploading = false;
                this.$message.error('文件上传失败: ' + (error.message || '服务器错误'));
                return false;
            }
        },
        close(targetPage = '/home') {
            const fullPath = this.$route.fullPath;
            const currentPath = this.$route.path;

            // 1. 先跳转到指定页面
            if (currentPath !== targetPage) {
                this.$router.push(targetPage).then(() => {
                    // 2. 跳转成功后延迟关闭当前标签页
                    setTimeout(() => {
                        this.closeTab(fullPath);
                    }, 100);
                }).catch(() => {
                    // 跳转失败时仍然尝试关闭
                    this.closeTab(fullPath);
                });
            } else {
                // 如果已经在目标页面，直接关闭
                this.closeTab(fullPath);
            }
        },

        // 提取关闭标签页的公共方法
        closeTab(fullPath) {
            this.$emit('close-tab', fullPath);

            let parent = this.$parent;
            while (parent) {
                if (typeof parent.removeTab === 'function') {
                    parent.removeTab(fullPath);
                    break;
                }
                parent = parent.$parent;
            }
        },
        // 提交表单
        async submitEvaluation() {
            // 验证必填项
            if (this.analysisFileList.length === 0) {
                this.$message.warning('请选择数据表字段文件');
                return;
            }

            // 检查是否有未解析的文件
            // const unparsedFiles = this.analysisFileList.filter(file => !file.parsed);
            // if (unparsedFiles.length > 0) {
            //     this.$message.warning('请先解析所有上传的文件');
            //     return;
            // }

            if (this.fileList.length === 0) {
                this.$message.warning('请选择抽样数据文件');
                return;
            }

            if (!this.coldDataMethod.trim()) {
                this.$message.warning('请输入冷数据处理方式');
                return;
            }


                        // 显示提交中弹窗 
            // this.submitConfirmInstance = this.$confirm('正在提交文件，请勿关闭或刷新页面...', '提交中', {
            //     confirmButtonText: '确定',
            //     type: 'info',
            //     showCancelButton: false,
            //     closeOnClickModal: false,
            //     closeOnPressEscape: false,
            //     // 禁用确定按钮
            //     confirmButtonDisabled: true,
            //     // 自定义样式 }}
            //     //{ 显示loading图标 }}
            //     showClose: false,
            //     beforeClose: (action, instance, done) => {
            //         // 防止用户手动关闭弹窗
            //         if (action === 'confirm') {
            //             done();
            //         }
            //     }
            // });
                        this.submitConfirmInstance = this.$loading({
                lock: true,
                text: '正在提交文件，请勿关闭或刷新页面...',
                background: 'rgba(0, 0, 0, 0.7)'
            });

            this.submitting = true;

            try {
                // 1. 先上传所有文件
                const uploadSuccess = await this.uploadAllFiles();
                if (!uploadSuccess) {
                    this.submitting = false;
                    return;
                }

                // 2. 提交表单数据
                const submitData = {
                    registrationId: this.registrationId,
                    dataFieldFile: this.uploadedFileUrl.dataFieldFile,
                    dataDetailedFile: this.uploadedFileUrl.dataDetailedFile,
                    oldDataHandle: this.coldDataMethod,
                    operator: this.userId,
                };

                const response = await this.$axios.post('/web/dataQualityDetail/submit', submitData, {
                    headers: {
                        'X-User-Id': this.userId
                    }
                });

                if (response.data && response.data.status === 200) {
                                       // 关闭提交中的弹窗
                    this.closeSubmitConfirm();
                    // 提交成功后显示确认弹窗
                    this.$confirm('提交成功！', '提示', {
                        confirmButtonText: '确定',
                        type: 'success',
                        showCancelButton: false,
                        closeOnClickModal: false,
                        closeOnPressEscape: false
                    }).then(() => {
                        this.close('/DataQuality');
                    });
                } else {
                    this.$message.error('提交失败: ' + (response.data.msg || '未知错误'));
                }
            } catch (error) {
                console.error('提交失败:', error);
                this.$message.error('提交失败: ' + (error.message || '服务器错误'));
            } finally {
                this.submitting = false;
            }
        },
                // 关闭提交确认弹窗的方法
        closeSubmitConfirm() {
            if (this.submitConfirmInstance) {
                this.submitConfirmInstance.close();
                this.submitConfirmInstance = null;
            }
        }
    },
    computed: {
        isSubmitDisabled() {
            // 提交按钮只有在未提交状态、有文件、有处理方式时可用
            return !(!this.submitting &&
                !this.uploading &&
                this.analysisFileList.length > 0 &&
                // this.analysisFileList.every(file => file.parsed) && // 所有文件都已解析
                this.fileList.length > 0 &&
                this.coldDataMethod.trim().length > 0);
        }
    }
}
</script>

<style scoped>
.data-evaluation-page {
    padding: 15px;
    background-color: #f5f5f5;
}

.box-card {
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.box-card>>>.el-card__header {
    background-color: #f0f0f0;
    font-weight: bold;
    font-size: 16px;
    color: #333;
}

.clearfix:before,
.clearfix:after {
    display: table;
    content: "";
}

.clearfix:after {
    clear: both;
}

.el-form-item {
    margin-bottom: 10px;
}

.el-form-item>>>.el-form-item__label {
    color: #666;
    font-weight: normal;
}
</style>